import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ArrowRight } from 'lucide-react';
import api from '../../api/axios';
import styles from './VerifyCode.module.css';

const VerifyCode = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || 'your email';
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.substring(value.length - 1);
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newCode[index] = char;
    });
    setCode(newCode);
    
    // Focus last filled input or the first empty one
    const nextIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextIndex].focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length !== 6) return;

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await api.post('/verify-forgot-password-code', { 
        email, 
        code: verificationCode 
      });
      
      setSuccess('Code verified successfully! Redirecting...');
      setTimeout(() => {
        navigate('/reset-password', { state: { email, code: verificationCode } });
      }, 1500);
    } catch (err) {
      console.error('Verification error:', err);
      if (err.response?.status === 422) {
        setError('The code you entered is incorrect or has expired.');
      } else {
        setError('Could not verify code. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/forgot-password', { email });
      setSuccess('A new verification code has been sent to your email.');
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['verify-code-page']}>
      <div className={styles['verify-code-card']}>
        <div className={styles['card-header-banner']}>
          <h1 className={styles['title']}>Verify Code</h1>
        </div>

        <div className={styles['card-content']}>
          <p className={styles['subtitle']}>
            We've sent a 6-digit verification code to <span className={styles['email-highlight']}>{email}</span>. 
            Enter the code below to continue.
          </p>

          {error && <div className={styles['error-message']}>{error}</div>}
          {success && <div className={styles['success-message']}>{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles['code-inputs']}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  className={styles['code-input']}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  required
                />
              ))}
            </div>

            <button type="submit" className={styles['submit-btn']} disabled={loading}>
              {loading ? 'Verifying...' : (
                <>
                  Verify Code
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className={styles['resend-section']}>
            <span className={styles['resend-text']}>Didn't receive the code?</span>
            <button 
              className={styles['resend-btn']} 
              type="button" 
              onClick={handleResendCode}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend Code'}
            </button>
          </div>

          <hr className={styles['divider']} />

          <Link to="/login" className={styles['back-link']}>
            <ArrowLeft size={16} /> Back to Login
          </Link>
        </div>
      </div>

    </div>
  );
};

export default VerifyCode;
